import {useState} from "react";
import styled from "styled-components/macro";

import {useParams} from "react-router-dom";
import {
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Stack,
    Textarea,
    useToast,
} from "@chakra-ui/react";
import {Select as MultiSelect,} from "chakra-react-select";
import Header from "../../../components/header";
import TopBar from "../../../components/top_bar";
import SortBar from "../../../components/sort_bar";
import CommunityHeader from "../../../components/community_header";
import Container from "../../../components/container";
import Main from '../../../components/main';
import Sidebar from "../../../components/sidebar";
import Footer from "../../../components/footer";
import Wrapper from "../../../components/wrapper";
import {useCommunitySettings, useCommunityStyle, useReloadCommunity, useSaveSettings,} from "../../../api/communities";
import {useBaseCommunity} from "../../../store/hooks";
import HeaderData from "../../../components/header_data";
import {useFeedback} from "../../../store/feedback";
import {isPseudoCommunity} from "../../../store/communites";
import {useCommunityDesignToken, useSetCommunityDesign} from "../../../api/create_image";
import CommunityInfo from "../../../components/community_info";
import useWindowDimensions from "../../../utils/dimensions";
import LegacySortBar from "../../../components/legacy/legacy_sort_bar";

const topics = [
    "Politics",
    "NSFW",
    "Memes",
    "Culture",
    "Sports",
    "Gaming",
    "Guns",
    "Hobbies",
    "Cars",
    "Videos",
    "Stories",
    "Technology",
    "Streaming",
    "Discussion",
    "Entertainment",
    "Music",
    "Movies",
    "TV",
    "Humor",
    "GIFs",
    "Lifestyle",
    "News",
    "Places",
    "Finance"
];

const Upload = styled.div``;
const UploadText = styled.div``;

const CommunitySettings = styled(({className}) => {
    const toast = useToast();
    const {error, feedbackCh} = useFeedback();
    const baseCommunity = useBaseCommunity();
    const {community: paramsCommunity} = useParams();
    const community = paramsCommunity || baseCommunity;
    const style = useCommunityStyle(community);

    const [settings, setSettings] = useState({
        displayName: '',
        tagline: '',
        description: '',
        metaTags: '',
        minScorePosts: 0,
        minScoreComments: 0,
        minScoreDownPosts: 0,
        minScoreDownComments: 0,
        visibility: 'public',
        defaultPostType: 'media',
        defaultCommentSort: 'top',
        allowTextPosts: false,
        allowImagePosts: false,
        allowLinkPosts: false,
        contentFilters: false,
        allowPostEditing: false,
        allowCommentEditing: false,
        allowComments: false,
        allowChildComments: false,
        publicLogs: false,
        obsfucatedLogs: false,
        notifyReportConclusion: false,
        banLogs: false,
        topics: [],
        appSafe: false,
    });
    const set = (name) => (value) => setSettings((settings) => ({...settings, [name]: value}));

    const [logo, setLogo] = useState(null);
    const [header, setHeader] = useState(null);

    const config = useCommunitySettings(feedbackCh, community, setSettings);

    const [saving, setSaving] = useState(false);

    const uploadToken = useCommunityDesignToken(feedbackCh);
    const setCommunityDesign = useSetCommunityDesign(feedbackCh);
    const saveSettings = useSaveSettings(feedbackCh);
    const reloadCommunity = useReloadCommunity(feedbackCh);
    const handleSave = async () => {
        if (saving) return;
        try {
            setSaving(true);
            await saveSettings(community, settings);
            if (logo || header) {
                const token = await uploadToken(community, header ? true : false, logo ? true : false);
                await setCommunityDesign(header, logo, token)
            }
            await reloadCommunity([community]);
        } catch {
            // error is displayed
        }
        setSaving(false);
        toast({
            title: `Saved settings for c/${community}!`,
            status: 'success',
            isClosable: true,
        });
    };

    const {width} = useWindowDimensions();

    return (
        <>
            <HeaderData title={`Community Settings`} community={community} community_style={style}/>
            <Header>
                {(baseCommunity !== "TheDonald" || width <= 780) && <TopBar legacyConversion={baseCommunity === "TheDonald"} />}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                        {baseCommunity !== "TheDonald" &&
                            <CommunityInfo desktop community={community}/>
                        }
                    </>
                }
                {baseCommunity === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community}/>
            <Container className={className} forceWidth={"slim"}>
                <Wrapper>
                    <Main>

                        <Stack spacing={'5'} maxWidth={"400px"}>

                            <Stack spacing={'1'}>
                                <FormLabel>Community Display Name</FormLabel>
                                <Input value={settings.displayName} placeholder="Cats"
                                       onChange={(e) => set('displayName')(e.currentTarget.value)}/>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Tagline</FormLabel>
                                <Input value={settings.tagline} placeholder="Cats are nice."
                                       onChange={(e) => set('tagline')(e.currentTarget.value)}/>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Description</FormLabel>
                                <Textarea rows={5} value={settings.description}
                                          placeholder="A community for discussing cats."
                                          onChange={(e) => set('description')(e.currentTarget.value)}/>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Meta Tags</FormLabel>
                                <Input value={settings.metaTags} placeholder="cats, animals, pets, meow"
                                       onChange={(e) => set('metaTags')(e.currentTarget.value)}/>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Community Topics</FormLabel>
                                <MultiSelect
                                    isMulti
                                    name="topics"
                                    value={settings.topics}
                                    options={topics}
                                    getOptionLabel={option => option}
                                    getOptionValue={option => option}
                                    placeholder="Select topics..."
                                    closeMenuOnSelect={false}
                                    onChange={set('topics')}
                                />
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Community Logo</FormLabel>
                                <FormControl>
                                    <Upload>
                                        <input type={"file"} onChange = {(e) => setLogo(e.target.files[0])}/>
                                        <FormHelperText>Maximum file size: 12MB (JPG/PNG/GIF)</FormHelperText>
                                    </Upload>
                                </FormControl>
                            </Stack>
                            <Stack spacing={'1'}>
                                <FormLabel>Community Header</FormLabel>
                                <FormControl>
                                    <Upload>
                                        <input type={"file"} onChange = {(e) => setHeader(e.target.files[0])}/>
                                        <FormHelperText>Maximum file size: 12MB (JPG/PNG/GIF)</FormHelperText>
                                    </Upload>
                                </FormControl>
                            </Stack>

                            <FormControl>
                                <Stack spacing={'1'}>
                                    <FormLabel>Min. Score to Vote on Posts</FormLabel>
                                    <NumberInput value={settings.minScorePosts} min={0} max={100}
                                                 onChange={(e) => set('minScorePosts')(e)}>
                                        <NumberInputField/>
                                        <NumberInputStepper>
                                            <NumberIncrementStepper/>
                                            <NumberDecrementStepper/>
                                        </NumberInputStepper>
                                    </NumberInput>
                                    <FormHelperText>Score settings will eventually be phased out.</FormHelperText>
                                </Stack>
                            </FormControl>

                            <Stack spacing={'1'}>
                                <FormLabel>Min. Score to Vote on Comments</FormLabel>
                                <NumberInput value={settings.minScoreComments} min={0} max={100}
                                             onChange={(e) => set('minScoreComments')(e)}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Min. Score to Downvote Posts</FormLabel>
                                <NumberInput value={settings.minScoreDownPosts} min={0} max={100}
                                             onChange={(e) => set('minScoreDownPosts')(e)}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Min. Score to Downvote Comments</FormLabel>
                                <NumberInput value={settings.minScoreDownComments} min={0} max={100}
                                             onChange={(e) => set('minScoreDownComments')(e)}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </Stack>

                            <FormControl>
                                <Stack spacing={'1'}>
                                    <FormLabel>Community Visibility</FormLabel>
                                    <Select value={settings.visibility}
                                            onChange={(e) => set('visibility')(e.currentTarget.value)}>
                                        <option value="public">Public</option>
                                        <option value="restricted">Restricted</option>
                                        <option value="unlisted">Unlisted</option>
                                    </Select>
                                    <FormHelperText>In restricted mode, only moderators can contribute.</FormHelperText>
                                </Stack>
                            </FormControl>

                            <Stack spacing={'1'}>
                                <FormLabel>Default Post Type</FormLabel>
                                <Select value={settings.defaultPostType}
                                        onChange={(e) => set('defaultPostType')(e.currentTarget.value)}>
                                    <option value="media">Media</option>
                                    <option value="link">Link</option>
                                    <option value="text">Text</option>
                                </Select>
                            </Stack>

                            <Stack spacing={'1'}>
                                <FormLabel>Default Comment Sort</FormLabel>
                                <Select value={settings.defaultCommentSort}
                                        onChange={(e) => set('defaultCommentSort')(e.currentTarget.value)}>
                                    <option value="top">Top</option>
                                    <option value="new">New</option>
                                    <option value="old">Old</option>
                                    <option value="controversial">Controversial</option>
                                </Select>
                            </Stack>

                            <Stack spacing={'3'}>
                                <Checkbox size={'sm'} isChecked={settings.appSafe}
                                          onChange={(e) => set('appSafe')(e.target.checked)}>Community is safe for App Stores</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowTextPosts}
                                          onChange={(e) => set('allowTextPosts')(e.target.checked)}>Allow Text
                                    Posts</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowLinkPosts}
                                          onChange={(e) => set('allowLinkPosts')(e.target.checked)}>Allow Link
                                    Posts</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowImagePosts}
                                          onChange={(e) => set('allowImagePosts')(e.target.checked)}>Allow Image
                                    Posts</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.contentFilters}
                                          onChange={(e) => set('contentFilters')(e.target.checked)}>Content Filters
                                    (legacy)</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowPostEditing}
                                          onChange={(e) => set('allowPostEditing')(e.target.checked)}>Allow Post
                                    Editing</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowComments}
                                          onChange={(e) => set('allowComments')(e.target.checked)}>Allow
                                    Comments</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowCommentEditing}
                                          onChange={(e) => set('allowCommentEditing')(e.target.checked)}>Allow Comment
                                    Editing</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.allowChildComments}
                                          onChange={(e) => set('allowChildComments')(e.target.checked)}>Allow Child
                                    Comments (replies)</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.publicLogs}
                                          onChange={(e) => set('publicLogs')(e.target.checked)}>Public Moderation
                                    Logs</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.obsfucatedLogs}
                                          onChange={(e) => set('obsfucatedLogs')(e.target.checked)}>Hide Moderators in
                                    Logs</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.banLogs}
                                          onChange={(e) => set('banLogs')(e.target.checked)}>Enable Ban Logs (public)</Checkbox>
                                <Checkbox size={'sm'} isChecked={settings.notifyReportConclusion}
                                          onChange={(e) => set('notifyReportConclusion')(e.target.checked)}>Notify
                                    Report Conclusion</Checkbox>
                            </Stack>

                            <Button variant="blue" mr={3} isLoading={saving} onClick={handleSave}>
                                Save
                            </Button>

                        </Stack>

                    </Main>
                </Wrapper>
                <Sidebar sidebar={style?.sidebar} community={community}/>
            </Container>
            <Footer/>
        </>
    );
})`
  ${Main} {
    padding: 24px;
  }
  label {
    padding-top: 8px;
  }
`;

export default CommunitySettings;
